import React from "react";
import { Button, Result, Spin } from "antd";
import { FormattedMessage } from "react-intl";
import {
  canFinishSetup,
  canRefreshStatus,
  getReturnCopy,
  needsSignIn,
  PAYOUT_RETURN_STATES,
  resolveReturnState,
} from "./returnState";

/**
 * What a browser shows when Stripe redirects back from hosted Connect
 * onboarding.
 *
 * The WebView gets its own panel — it can hand the user straight back to the
 * app. A browser cannot, so this one has to stand on its own: say what actually
 * happened, and give the one action that makes sense next. Previously this
 * route sat behind the auth gate, so a returning browser matched no route at
 * all and rendered a blank page.
 *
 * Which state applies is decided in `returnState.js`, where it can be tested
 * without a DOM.
 */
export const BrowserReturnPanel = ({
  payoutsEnabled,
  detailsSubmitted,
  statusLoaded,
  signedIn,
  onFinishSetup,
  onRefresh,
  loading,
}) => {
  const state = resolveReturnState({
    payoutsEnabled,
    detailsSubmitted,
    statusLoaded,
    signedIn,
  });
  const { title, description } = getReturnCopy(state);

  // `info` rather than `warning` for the unfinished case: the user has done
  // nothing wrong, they just stopped partway.
  const status = state === PAYOUT_RETURN_STATES.COMPLETE ? "success" : "info";

  const actions = [
    /* Only when something is genuinely outstanding. Offering it to a verified
       user invites them to redo setup; offering it to a signed-out one 401s,
       and the interceptor clears storage and bounces them to the homepage. */
    canFinishSetup(state) && (
      <Button
        key="finish"
        type="primary"
        className="fw-bold"
        loading={loading}
        onClick={onFinishSetup}
        data-testid="finish-payout-setup"
      >
        <FormattedMessage id="FINISH_PAYOUT_SETUP" />
      </Button>
    ),
    needsSignIn(state) && (
      <Button key="signin" type="primary" className="fw-bold" href="/">
        <FormattedMessage id="SIGN_IN_TO_CONTINUE" />
      </Button>
    ),
    /* The status poll gives up after ~96s, so without this someone still
       verifying has no way to re-check short of reloading the page. */
    canRefreshStatus(state) && onRefresh && (
      <Button
        key="refresh"
        onClick={onRefresh}
        loading={loading}
        data-testid="refresh-payout-status"
      >
        <FormattedMessage id="REFRESH_STATUS" />
      </Button>
    ),
    <Button key="back" type="link" href="/">
      <FormattedMessage id="BACK_TO_GIGMI" />
    </Button>,
  ].filter(Boolean);

  return (
    <Result
      status={status}
      icon={
        state === PAYOUT_RETURN_STATES.LOADING ? (
          <Spin size="large" />
        ) : undefined
      }
      title={<FormattedMessage id={title} />}
      subTitle={<FormattedMessage id={description} />}
      extra={actions}
    />
  );
};

export default BrowserReturnPanel;
